/** @prettier */

import {useLocation, useParams, Link} from 'react-router-dom';
import {Card, CardContent, Grid, Stack, Box} from '@mui/material';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

//MTH component import
import OUAddDialog from './OUAddDialog';
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import OUDeleteDialog from './OUDeleteDialog';
import OUEditDialog from './OUEditDialog';
import CardContents from '../../component/CardContents';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack} from '../../define';

/**
 * SL通信設定の室外機一覧
 * @param props props
 * @returns JSX.Element
 */
const OUSetting = (props: {
  /** 室内外機の情報 */
  slInfo: apiIF.res.getCompositionsSLUnit | null;
  /** 室内外機の情報（set関数） */
  setSLInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsSLUnit | null>
  >;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
  /** SL新旧設定 */
  slType: 'new' | 'old';
}) => {
  const trans = useTranslation().t; //翻訳関数
  //URL取得
  const location = useLocation();
  //パスパラメータ取得
  const {systemType: slType} = useParams();

  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false); //編集ダイアログ表示フラグ
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false); //削除ダイアログ表示フラグ
  const [selectedOUID, setSelectedOUID] = useState(''); //選択した室外機のID

  /** 室外機追加ボタンの名前 */
  const addButtonName = trans('OUSetting.buttonTxt.addUnit');
  /** 室外機タイプ変換テーブル */
  const ouTypeTable: {[key: string]: string} = {
    'BeforeKXE6': trans('OUSetting.item.value.unitType.beforeKXE6'),
    'KXZ': trans('OUSetting.item.value.unitType.kxz'),
    'KXZ2': trans('OUSetting.item.value.unitType.kxz2'),
    '': trans('OUSetting.item.value.unitType.none'),
  };
  /** 電源の種類変換テーブル */
  const powerSupplyTable: {[key: string]: string} = {
    singlePhase: trans('OUSetting.item.value.powerSupply.singlePhase'),
    threePhase: trans('OUSetting.item.value.powerSupply.threePhase'),
  };

  /**
   * 登録されている室外機の台数をカウント
   * @returns 台数
   */
  const ouNumCount = () => {
    //パスパラメータを取得できない場合
    //室内外機の情報を取得できない場合
    if (slType === undefined || props.slInfo === null) {
      return 0;
    }

    return props.slInfo['outdoor-unit-informations'].reduce(
      (result, ouInfo) => {
        //表示している系統番号のみカウント
        if (ouInfo['sl-system'] === slType.slice(2)) {
          result++;
        }
        return result;
      },
      0
    );
  };

  /**
   * Editボタン押下時処理
   * @param ouID 室外機ID
   */
  const openEditDialog = (ouID: string) => {
    setIsOpenEditDialog(true);
    setSelectedOUID(ouID);
  };
  /**
   * Deleteボタン押下時処理
   * @param ouID 室外機ID
   */
  const openDeleteDialog = (ouID: string) => {
    setIsOpenDeleteDialog(true);
    setSelectedOUID(ouID);
  };

  /**
   * 親室外機の名前を取得
   * @param parentID 親室外機ID
   * @returns 親室外機の名前
   */
  const getParentUnitName = (parentID: string) => {
    if (props.slInfo === null) {
      return trans('OUSetting.item.value.parentUnit.none');
    }

    for (const ouInfo of props.slInfo['outdoor-unit-informations']) {
      if (
        ouInfo['outdoor-id'] === parentID &&
        ouInfo['outdoor-unit-name'] !== undefined
      ) {
        return ouInfo['outdoor-unit-name'];
      }
    }
    return trans('OUSetting.item.value.parentUnit.none');
  };

  /**
   * 歯車メニューリスト
   * @param ouID 室外機ID
   * @returns メニュー一覧
   */
  const gearMenuItem = (ouID: string): GearMenuListArgType['menuItem'] => {
    return [
      {
        id: `mv-ouEdit${ouID}`,
        label: trans('OUSetting.menuItem.edit'),
        onClick: () => openEditDialog(ouID),
        icon: {
          className: 'gwSetting__contents__button__icon',
          src: iconBlack.EDIT.src,
          alt: trans(`OUSetting.image.alternativeText.${iconBlack.EDIT.alt}`),
        },
      },
      {
        id: `mv-ouDelete${ouID}`,
        label: trans('OUSetting.menuItem.delete'),
        onClick: () => openDeleteDialog(ouID),
        icon: {
          className: 'gwSetting__contents__button__icon',
          src: iconBlack.DELETE.src,
          alt: trans(`OUSetting.image.alternativeText.${iconBlack.DELETE.alt}`),
        },
      },
    ];
  };

  //室内外機の情報を取得できない場合
  //パスパラメータを取得できない場合
  if (props.slInfo == null || slType === undefined) {
    return <></>;
  }
  return (
    <>
      {/* 室外機追加ボタン */}
      <Box className="gwSetting__contents--unit gwSetting__contents__button--add">
        <OUAddDialog
          addButtonName={addButtonName}
          slInfo={props.slInfo}
          setSLInfo={props.setSLInfo}
          isBilling={props.isBilling}
          setHasConfigGW={props.setHasConfigGW}
          slType={props.slType}
        />
      </Box>
      {/* 登録済室外機が無い場合は、室外機追加ボタンのみ表示 */}
      {ouNumCount() === 0 ? (
        <Card className="gwSetting__contents--unit">
          <CardContent className="gwSetting__contents__noRegister">
            {trans('OUSetting.instruction', {name: addButtonName})}
          </CardContent>
        </Card>
      ) : (
        props.slInfo['outdoor-unit-informations']
          //表示している系統番号の室外機のみ表示
          .filter(ouInfo => ouInfo['sl-system'] === slType.slice(2))
          .map(ouInfo => (
            <Card
              id={`mv-boxOu${ouInfo['outdoor-id']}`}
              key={`mv-boxOu${ouInfo['outdoor-id']}`}
              className="gwSetting__contents--unit">
              <CardContent>
                <Stack spacing={2}>
                  {/* 室外機名称と歯車 */}
                  <Grid
                    container
                    justifyContent="space-between"
                    className="gwSetting__contents__title">
                    {/* 室外機名称 */}
                    <Grid item xs="auto">
                      <Link
                        to={`${location.pathname}/${ouInfo['outdoor-id']}/sl-iu-registration${location.search}`}
                        id={`mv-linkIuSetting${ouInfo['outdoor-unit-name']}`}>
                        {ouInfo['outdoor-unit-name']}
                      </Link>
                    </Grid>
                    {/* 歯車ボタン */}
                    <Grid item xs={1}>
                      <GearMenu
                        id={`mv-btnOUSettingOpen${ouInfo['outdoor-id']}`}
                        menuItem={gearMenuItem(ouInfo['outdoor-id'])}
                      />
                      {/* ouIDをチェックして選択した室外機のダイアログのみ出す */}
                      {ouInfo['outdoor-id'] === selectedOUID &&
                      ouInfo['outdoor-unit-name'] !== undefined ? (
                        <>
                          <OUEditDialog
                            ouID={ouInfo['outdoor-id']}
                            displayOUInfo={ouInfo}
                            setSLInfo={props.setSLInfo}
                            isOpenDialog={isOpenEditDialog}
                            setIsOpenDialog={setIsOpenEditDialog}
                            isBilling={props.isBilling}
                            setHasConfigGW={props.setHasConfigGW}
                          />
                          <OUDeleteDialog
                            ouID={ouInfo['outdoor-id']}
                            ouName={ouInfo['outdoor-unit-name']}
                            setSLInfo={props.setSLInfo}
                            isOpenDialog={isOpenDeleteDialog}
                            setIsOpenDialog={setIsOpenDeleteDialog}
                            isBilling={props.isBilling}
                            setHasConfigGW={props.setHasConfigGW}
                          />
                        </>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                  {/* 室外機の情報 */}
                  <Stack spacing={2} className="gwSetting__contents__contents">
                    {/* SLアドレス */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'OUSetting.item.title.superlinkAddress'
                      )}
                      value={
                        ouInfo['sl-address'] === undefined
                          ? ''
                          : ouInfo['sl-address']
                      }
                    />
                    {/* 入力電圧 */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans('OUSetting.item.title.voltage')}
                      value={
                        ouInfo['input-voltage'] === undefined
                          ? ''
                          : ouInfo['input-voltage']
                      }
                    />
                    {/* 単相三相 */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'OUSetting.item.title.powerSupply'
                      )}
                      value={
                        ouInfo['power-supply'] === undefined
                          ? ''
                          : powerSupplyTable[ouInfo['power-supply']]
                      }
                    />
                    {/* 親室外機 */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'OUSetting.item.title.parentUnit'
                      )}
                      value={
                        ouInfo['parent-outdoor-unit'] === undefined
                          ? `${trans('OUSetting.item.value.parentUnit.none')}`
                          : getParentUnitName(ouInfo['parent-outdoor-unit'])
                      }
                    />
                    {/* 室外機タイプ（1st stepでは非表示とする） */}
                    {/* <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription="Outdoor unit type"
                      value={
                        ouInfo['outdoor-unit-type'] === undefined
                          ? 'None'
                          : ouTypeTable[ouInfo['outdoor-unit-type']]
                      }
                    /> */}
                    {/* 接続室内機 */}
                    <CardContents
                      class="gwSetting__contents__contents__underLine"
                      valueDescription={trans(
                        'OUSetting.item.title.indoorUnit'
                      )}
                      value={
                        ouInfo['indoor-unit-informations'] === undefined ? (
                          <></>
                        ) : (
                          <Stack spacing={0}>
                            {ouInfo['indoor-unit-informations'].map(iuInfo => (
                              <Box key={iuInfo['unit-id']}>
                                {iuInfo['indoor-unit-name']}
                              </Box>
                            ))}
                          </Stack>
                        )
                      }
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))
      )}
    </>
  );
};
export default OUSetting;
